import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Eye, CheckCircle2, XCircle, RotateCcw, Search, FlaskConical } from 'lucide-react';
import { Layout } from '@/components/Layout';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Button } from '@/components/ui/Button';
import { SortableColumn, useTableSort } from '@/components/ui/SortableColumn';
import { api } from '@/lib/api';
import { formatDate } from '@/lib/utils';

// Ícones por tipo de alvo — visual rápido pra Pablo saber do que se trata sem ler o texto.
const TARGET_TYPE_LABEL: Record<string, string> = {
  track:   'música',
  clip:    'clipe',
  profile: 'perfil',
  comment: 'comentário',
};

// Motivo da denúncia: CÓDIGO vindo do app, frase montada aqui.
//
// ⚠️ Até a v1.10.0 o app mandava o RÓTULO JÁ TRADUZIDO como `reason`
// (`ReportReasonSheet` chamava `onPick(label)`). Quem denunciava num telefone
// em inglês gravava `Other reason`; em espanhol, `Otro motivo`. O painel
// mostrava fielmente o que havia chegado — e por isso aparecia inglês aqui.
//
// 🔑 É o mesmo erro que a telemetria do cadastro já tinha pago: **categoria,
// nunca valor**. Motivo gravado como frase traduzida não agrupa, não conta e
// não compara; e o idioma de quem denunciou não é dado de moderação.
//
// O `?? r.reason` NÃO é defensividade: as denúncias anteriores à v1.10.0 estão
// no banco com a frase, em três idiomas, e continuam precisando aparecer.
const REASON_LABEL: Record<string, string> = {
  spam:          'Spam ou propaganda',
  inappropriate: 'Conteúdo impróprio',
  harassment:    'Assédio ou bullying',
  fake:          'Conta falsa ou impersonação',
  violence:      'Violência ou ódio',
  copyright:     'Violação de direitos autorais',
  // 31/10/2026 — da cláusula 7.4 dos Termos v2.5. O painel precisa conhecer a
  // chave no MESMO dia em que o app passa a enviá-la: sem isto, o
  // `?? r.reason` mostraria `uso_comercial` cru na tela de quem modera.
  uso_comercial: 'Uso comercial não permitido',
  other:         'Outro motivo',
};

const reasonLabel = (reason: string | null): string => {
  if (!reason) return '—';
  return REASON_LABEL[reason] ?? reason;
};

// Status válidos do schema content_reports:
//   pending    — aberta, ainda não foi olhada
//   reviewing  — em análise
//   actioned   — procedente, ação tomada (remover conteúdo, suspender user)
//   dismissed  — improcedente, denúncia descartada
type Status = 'pending' | 'reviewing' | 'dismissed' | 'actioned';

const STATUS_LABEL: Record<Status, string> = {
  pending:   'pendente',
  reviewing: 'em análise',
  actioned:  'procedente',
  dismissed: 'improcedente',
};

const STATUS_STYLES: Record<Status, string> = {
  pending:   'bg-amber-950 text-amber-300',
  reviewing: 'bg-sky-950 text-sky-300',
  actioned:  'bg-emerald-950 text-emerald-300',
  dismissed: 'bg-zinc-800 text-zinc-300',
};

export function Reports() {
  const qc = useQueryClient();
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [includeTest, setIncludeTest] = useState(false);
  const [debouncedSearch, setDebouncedSearch] = useState('');
  // Debounce 300ms — evita 1 request por keystroke enquanto Pablo digita.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const { data, isLoading } = useQuery({
    queryKey: ['admin-reports', status, debouncedSearch, includeTest],
    queryFn: async () => {
      const params: any = { limit: 100 };
      if (status) params.status = status;
      if (debouncedSearch) params.search = debouncedSearch;
      if (includeTest) params.includeTest = 'true';
      const { data } = await api.get('/admin/reports', { params });
      return Array.isArray(data?.data) ? data.data : [];
    },
  });

  const updateStatus = useMutation({
    mutationFn: ({ id, status }: { id: string; status: Status }) =>
      api.patch(`/admin/reports/${id}`, { status }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-reports'] }),
  });

  const rawItems: any[] = data ?? [];
  // 🧒 SEM ordenação padrão de propósito (31/10/2026). O servidor devolve a fila
  // já priorizada — pendentes primeiro, depois denúncias sobre conteúdo de
  // MENOR, depois por data. É a cláusula 4.5 dos Termos (moderação reativa
  // priorizada) virando ordem de atendimento.
  //
  // 🔴 Com `'created_at', 'desc'` aqui, o painel reordenava tudo no navegador e
  // a prioridade do servidor sumia antes de chegar aos olhos de quem modera —
  // a correção no backend teria ficado invisível. Clicar numa coluna continua
  // reordenando; por isso a linha prioritária também é MARCADA, e não depende
  // só da posição.
  const { sorted: items, sort, handleSort } = useTableSort(rawItems, null, 'desc', (row, field) => {
    if (field === 'reporter') return row.reporter_display_name || row.reporter_username;
    if (field === 'target') return row.target_type;
    return row[field];
  });

  return (
    <Layout title="Denúncias">
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Select value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">Status: todos</option>
          <option value="pending">Pendentes</option>
          <option value="reviewing">Em análise</option>
          <option value="actioned">Procedentes</option>
          <option value="dismissed">Improcedentes</option>
        </Select>
        <div className="relative min-w-[240px] flex-1">
          <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
          <Input
            className="pl-9"
            placeholder="Buscar por motivo, descrição ou reporter…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <label className="flex cursor-pointer items-center gap-2 rounded-md border border-border bg-bg-input px-3 py-2 text-xs text-text-secondary transition-colors hover:border-brand hover:text-text-primary">
          <input
            type="checkbox"
            checked={includeTest}
            onChange={(e) => setIncludeTest(e.target.checked)}
            className="h-3.5 w-3.5 accent-brand"
          />
          <FlaskConical size={12} />
          Mostrar test accounts (@reviewer)
        </label>
      </div>
      <p className="mb-4 text-xs text-text-muted">
        Ação no conteúdo denunciado (remover/suspender) deve ser feita na aba <span className="text-brand">Conteúdo</span> ou <span className="text-brand">Usuários</span>. Aqui você apenas marca o desfecho da denúncia.
      </p>

      <Card className="p-0 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="border-b border-border bg-bg-input text-left text-xs uppercase text-text-secondary">
            <tr>
              <SortableColumn label="Alvo" field="target" sort={sort} onSort={handleSort} />
              <SortableColumn label="Motivo" field="reason" sort={sort} onSort={handleSort} />
              <SortableColumn label="Reporter" field="reporter" sort={sort} onSort={handleSort} />
              <SortableColumn label="Status" field="status" sort={sort} onSort={handleSort} />
              <SortableColumn label="Quando" field="created_at" sort={sort} onSort={handleSort} />
              <th className="px-4 py-3 text-right">Ações</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr><td colSpan={6} className="p-8 text-center text-text-muted">Carregando…</td></tr>
            )}
            {items.map((r) => {
              const isTerminal = r.status === 'actioned' || r.status === 'dismissed';
              const targetLabel = TARGET_TYPE_LABEL[r.target_type] || r.target_type;
              // Backend enriched fields (2026-08-15): target_title / target_preview_url
              // / target_author_username vêm do listReports com correlated subqueries
              // por target_type. Se conteúdo foi apagado (deleteTrack), campos vêm
              // null — mostramos "conteúdo removido" pra Pablo saber o contexto.
              const wasRemoved = !r.target_title && !r.target_author_username;
              return (
                <tr key={r.id} className="border-b border-border last:border-0 align-top">
                  <td className="px-4 py-3">
                    <div className="flex items-start gap-3">
                      {r.target_preview_url ? (
                        <img
                          src={r.target_preview_url}
                          alt=""
                          className="h-10 w-10 flex-shrink-0 rounded object-cover"
                          loading="lazy"
                        />
                      ) : (
                        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded bg-bg-input text-[10px] uppercase text-text-muted">
                          {targetLabel.slice(0, 3)}
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="text-[10px] uppercase tracking-wide text-text-muted">{targetLabel}</div>
                        {wasRemoved ? (
                          <div className="text-sm italic text-text-muted">conteúdo removido</div>
                        ) : (
                          <>
                            <div className="truncate text-sm text-text-primary" title={r.target_title || ''}>
                              {r.target_title || '—'}
                            </div>
                            {r.target_author_username && (
                              <div className="truncate text-xs text-text-secondary">@{r.target_author_username}</div>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {/* 🧒 A marca da prioridade. Ela acompanha a LINHA, não a
                        posição: quem clicar numa coluna para reordenar continua
                        vendo qual denúncia é de conteúdo de menor. */}
                    {r.envolve_menor && (
                      <span className="mr-2 rounded bg-rose-950 px-1.5 py-0.5 text-[11px] font-medium text-rose-300">
                        menor · prioridade
                      </span>
                    )}
                    <div className="inline text-text-primary">{reasonLabel(r.reason)}</div>
                    {r.description && (
                      <div className="mt-1 line-clamp-2 max-w-md text-xs text-text-muted">{r.description}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-text-secondary">
                    <div className="flex items-center gap-1.5">
                      <span>@{r.reporter || 'anônimo'}</span>
                      {r.reporter_is_test && (
                        <span
                          className="inline-flex items-center gap-0.5 rounded bg-purple-950/50 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-purple-300"
                          title="Test account Apple/Google App Review — denúncia não é real"
                        >
                          <FlaskConical size={9} /> test
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`rounded-md px-2 py-0.5 text-xs ${STATUS_STYLES[r.status as Status] || 'bg-zinc-800 text-zinc-300'}`}>
                      {STATUS_LABEL[r.status as Status] || r.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-text-secondary">{formatDate(r.created_at)}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex gap-1">
                      {!isTerminal && r.status !== 'reviewing' && (
                        <Button
                          size="sm"
                          variant="ghost"
                          title="Marcar em análise"
                          onClick={() => updateStatus.mutate({ id: r.id, status: 'reviewing' })}
                        >
                          <Eye size={14} />
                        </Button>
                      )}
                      {!isTerminal && (
                        <>
                          <Button
                            size="sm"
                            variant="ghost"
                            title="Procedente (denúncia válida — lembre de agir no conteúdo)"
                            onClick={() => updateStatus.mutate({ id: r.id, status: 'actioned' })}
                          >
                            <CheckCircle2 size={14} className="text-emerald-400" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            title="Improcedente (descartar denúncia)"
                            onClick={() => updateStatus.mutate({ id: r.id, status: 'dismissed' })}
                          >
                            <XCircle size={14} className="text-zinc-400" />
                          </Button>
                        </>
                      )}
                      {isTerminal && (
                        <Button
                          size="sm"
                          variant="ghost"
                          title="Reabrir denúncia"
                          onClick={() => updateStatus.mutate({ id: r.id, status: 'pending' })}
                        >
                          <RotateCcw size={14} />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
            {!isLoading && items.length === 0 && (
              <tr><td colSpan={6} className="p-8 text-center text-text-muted">Nenhuma denúncia encontrada.</td></tr>
            )}
          </tbody>
        </table>
      </Card>
    </Layout>
  );
}
